import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { FaArrowLeft, FaPrint, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const PettyCashView = () => {
  const [pettyCashList, setPettyCashList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  useEffect(() => {
    fetchPettyCashList();
  }, []);

  const fetchPettyCashList = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
      const companyCode = localStorage.getItem("companyCode") || "-";
      const currentDepartment =
        localStorage.getItem("currentSelectedDepartment") || "MAIN";

      // Replace with your petty cash API endpoint
      //   const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMDocListView/${companyCode}/JOURNAL/PC/-/-/-/-/${currentDepartment}/1/50`;
      const apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMDocListView/${COMPANY_CODE}/JOURNAL/PC/-/-/-/-/${currentDepartment}/1/50`;

      const response = await axios.get(apiUrl);
      console.log("Fetched Petty Cash List:", response.data);

      setPettyCashList(response.data);
      setFilteredList(response.data);
    } catch (err) {
      console.error("Error fetching petty cash list:", err);
      setError("Failed to fetch petty cash list. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = pettyCashList.filter((item) => {
      const formattedDate = dayjs(item.V_DATE).format("DD/MM/YYYY");
      const formattedAmount = item.AMOUNT?.toFixed(2) ?? "";

      return (
        item.DESRIPTION?.toLowerCase().includes(term) ||
        item.V_NO?.toLowerCase().includes(term) ||
        item.USER?.toLowerCase().includes(term) ||
        item.JVTYPE?.toLowerCase().includes(term) ||
        formattedDate.includes(term) ||
        formattedAmount.includes(term)
      );
    });

    setFilteredList(filtered);
  };

  const onBack = () => {
    navigate("/pettyCash");
  };

  const onPrint = (data) => {
    const vNo = data.V_NO.trim();
    const vType = data.JVTYPE;
    const vDate = new Date(data.V_DATE).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // fetchPCPrintData(vNo, vType, vDate);

    navigate(`/pettyCashPrint`, { state: { pcPrintData: vNo } });
  };

  // const fetchPCPrintData = async (jvNo, jvType, jvDate) => {
  //   try {
  //     const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
  //     const companyCode = localStorage.getItem("companyCode") || "-";
  //     const currentDepartment =
  //       localStorage.getItem("currentSelectedDepartment") || "MAIN";

  //     const apiURL = `${apiPaths[0]?.API_CONFIG}/api/CRMReportGenerator/${companyCode}/VOUCHER/${jvNo}/${currentDepartment}/-/${jvType}/-/-/-`;

  //     const response = await axios.get(apiURL);
  //     console.log("Fetched Petty Cash Print Data:", response.data);

  //     navigate(`/pettyCashPrint`, {
  //       state: { pcPrintData: response.data, jvNo, jvType, jvDate },
  //     });
  //   } catch (err) {
  //     console.error("Error fetching petty cash print data:", err);
  //     setError("Failed to fetch petty cash print data. Please try again.");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const onEdit = (rec) => {
    console.log("🚀 ~ onEdit ~ rec:", rec);

    const modifyVoucherData = {
      modifyVNO: rec.V_NO?.trim(),
      modifyVType: rec.JVTYPE,
      modifyVDate: dayjs(rec.V_DATE).format("YYYY-MM-DD"),
      amount: rec.AMOUNT,
      description: rec.DESRIPTION,
    };

    navigate("/voucher", { state: { modifyVoucherData } });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header + Search */}
      <div className="flex justify-between items-center gap-6 mb-4">
        <h2 className="text-xl text-gray-800 ">Petty Cash View</h2>

        <div className="relative w-[40%]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by any field..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        <button
          onClick={onBack}
          className="relative bg-gray-600 text-white font-medium text-[17px] px-3 py-[0.4em] pl-4 h-[2em] rounded-[0.9em] flex items-center overflow-hidden cursor-pointer group"
        >
          <span className="mr-10">Back</span>
          <div className="absolute right-[0.3em] bg-white h-[1.4em] w-[1.4em] rounded-[0.7em] flex items-center justify-center transition-all duration-300 group-hover:w-[calc(100%-0.6em)]">
            <FaArrowLeft className="text-gray-600" />
          </div>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-10 h-10 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  Voucher No
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  Description
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  User
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  JV Type
                </th>
                {/* <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                  Mode
                </th> */}
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 border-b">
                  Amount
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 border-b">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredList.length > 0 ? (
                filteredList.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-blue-50 transition-colors border-b"
                  >
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {item.V_NO.trim()}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {dayjs(item.V_DATE).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {item.DESRIPTION || "-"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-left">
                      {item.USER || "-"}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-left">
                      {item.JVTYPE || "-"}
                    </td>
                    {/* <td className="px-4 py-2 text-sm text-gray-700">
                      {item.MODE || "-"}
                    </td> */}
                    <td className="px-4 py-2 text-sm text-gray-700 text-right">
                      {item.AMOUNT.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => onEdit(item)}
                          className="px-2 py-1 rounded text-white bg-blue-600 flex items-center gap-1 text-sm"
                        >
                          <FaEdit className="text-white" size={12} />
                          Edit
                        </button>
                        <button
                          onClick={() => onPrint(item)}
                          className="px-2 py-1 rounded text-white bg-yellow-600 flex items-center gap-1 text-sm float-right"
                        >
                          <FaPrint size={12} />
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="text-center py-4 text-gray-500 text-sm"
                  >
                    No Petty Cash records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PettyCashView;
